interface GoogleCredentialResponse {
  credential?: string
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(options: {
        client_id: string
        callback: (response: GoogleCredentialResponse) => void
      }): void
      prompt(): void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

const SCRIPT_ID = 'kuniri-google-identity'
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'
const PROMPT_TIMEOUT_MS = 60_000

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Google Identity Services needs a browser'))
  }
  if (window.google?.accounts?.id) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('load failed')), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error('load failed')), {
      once: true,
    })
    document.head.appendChild(script)
  }).catch((error: unknown) => {
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

/**
 * Requests a Google ID token through Google Identity Services (D-027).
 * Enabled only when `VITE_GOOGLE_OAUTH_ENABLED=true` and a client ID exists
 * (D-044); otherwise the UI shows a disabled provider with a clear message.
 */
export async function requestGoogleIdToken(clientId: string): Promise<string> {
  await loadScript()
  const google = window.google
  if (!google?.accounts?.id) {
    throw new Error('Google Identity Services unavailable')
  }

  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Google sign-in timed out')),
      PROMPT_TIMEOUT_MS,
    )

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        clearTimeout(timer)
        if (response.credential) {
          resolve(response.credential)
        } else {
          reject(new Error('Google returned no credential'))
        }
      },
    })
    google.accounts.id.prompt()
  })
}
